import { useId } from 'react';
import { DatePicker } from '@roger/ui-components';

import { formatDateToISO, parseISOToLocalDate } from '../../../utils/dateUtils';

interface WorksheetDateInputProps {
  label: string;
  value?: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
}

/**
 * Labeled worksheet date field: calendar popup + manual typing, commits on
 * blur/Enter, saves bare YYYY-MM-DD strings and emits null (never '') on clear.
 */
export function WorksheetDateInput({
  label,
  value,
  onChange,
  disabled,
}: WorksheetDateInputProps) {
  // Bind the label to the typable input by id, and don't wrap the picker in
  // the label: a wrapping <label> names the popover trigger button (the first
  // labelable element inside it) instead of the text input.
  const inputId = useId();

  return (
    // No space-y here: sibling fields wrap a bare text node in their <label>,
    // so their control gets no top margin. An explicit <label> element would
    // make space-y apply and push date fields 4px below their row neighbours.
    <div className='text-xs text-muted-foreground'>
      <label htmlFor={inputId} className='block'>
        {label}
      </label>
      <DatePicker
        id={inputId}
        datePickerSize='sm'
        typable
        className='w-full'
        disabled={disabled}
        date={parseISOToLocalDate(value ?? '')}
        onDateChange={date => onChange(date ? formatDateToISO(date) : null)}
      />
    </div>
  );
}
